import { createContext, useState, useEffect, useRef } from "react";
import apiService from "../services/apiService";
import configService from "../services/configService";
import { getClientConfig } from "../services/clientConfigCache";

export const LoginContext = createContext();

export const LoginProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [apiToken, setApiToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  // Ref per prevenire login multipli - IMPORTANTE!
  const isLoginInProgress = useRef(false);
  const hasInitialized = useRef(false);

  // 🔐 Ottieni email e password in modo sicuro dalla configurazione server
  const getClientCredentials = async () => {
    try {
      const config = await getClientConfig();
      if (config) {
        const email =
          config.REACT_APP_API_EMAIL ||
          window.REACT_APP_API_EMAIL ||
          process.env.REACT_APP_API_EMAIL;

        let password =
          config.REACT_APP_API_PASSWORD ||
          process.env.REACT_APP_API_PASSWORD ||
          "ABQJthtHghT784N";

        // Alcuni ambienti/devtool possono trasformare '$' in '\$' (dotenv-expand / escaping).
        // Il backend si aspetta '$' letterale.
        if (typeof password === "string" && password.includes("\\$")) {
          password = password.replaceAll("\\$", "$");
        }

        if (typeof password === "string") {
          password = password.trim();
        }
        console.log("[Autologin] Credenziali caricate:", {
          hasEmail: Boolean(email),
          passwordLen: typeof password === "string" ? password.length : 0,
          passwordHasDollar: typeof password === "string" ? password.includes("$") : false,
          passwordHasBackslash: typeof password === "string" ? password.includes("\\") : false,
          // preview mascherata per capire se contiene '\$' o '$' senza esporre la password
          passwordPreview:
            typeof password === "string"
              ? `${password.slice(0, 2)}***${password.slice(-2)}`
              : "",
        });
        return { email, password };
      }
    } catch (error) {
      console.error('Errore nel caricamento configurazione:', error);
    }
    return {
      email: window.REACT_APP_API_EMAIL || process.env.REACT_APP_API_EMAIL,
      password: process.env.REACT_APP_API_PASSWORD || 'ABQJthtHghT784N'
    };
  };

  // Funzione esterna per l'autologin
  const autologin = async ({
    setApiToken,
    setIsAuthenticated,
    setApiError,
    setLoading
  }) => {
    // CONTROLLO CRITICO: previeni esecuzioni multiple
    if (isLoginInProgress.current) {
      console.log("[Autologin] Login già in corso, skip...");
      return;
    }

    // Imposta i flag PRIMA di iniziare
    isLoginInProgress.current = true;
    hasInitialized.current = true;

    // PULIZIA STORAGE: Rimuovi solo le informazioni di destinazione, mantieni apiToken se valido
    console.log("[Autologin] Pulizia parziale storage...");
    localStorage.removeItem("destinazione");
    localStorage.removeItem("codiceCliente");
    localStorage.removeItem("codiceDestinazione");
    // NON rimuoviamo apiToken e companyInfo qui, potrebbero essere validi

    try {
      // Ottieni email e password del client
      const { email: API_EMAIL, password: API_PASSWORD } = await getClientCredentials();

      // Verifica che le credenziali API siano presenti
      if (!API_EMAIL || !API_PASSWORD) {
        console.error("[Autologin] Credenziali API mancanti. Verifica le variabili d'ambiente.");
        setApiError("Configurazione autologin non valida.");
        setLoading(false);
        return;
      }

      console.log("[Autologin] Inizio autologin con email:", API_EMAIL);

      // Ottieni il token CSRF con gestione degli errori
      console.log("[Autologin] Richiesta token CSRF...");
      try {
        const csrfResponse = await apiService("/sanctum/csrf-cookie", "GET");
        console.log("[Autologin] Risposta CSRF ricevuta:", csrfResponse);
      } catch (csrfError) {
        console.warn("[Autologin] Errore nella richiesta CSRF, proseguo comunque:", csrfError.message);
      }

      // Piccolo delay per assicurare che il cookie CSRF sia settato
      await new Promise(resolve => setTimeout(resolve, 100));

      // Esegui la richiesta di login
      console.log("[Autologin] Invio richiesta di login...");
      const response = await apiService("/users/login", "POST", {
        email: API_EMAIL,
        password: API_PASSWORD,
      });

      console.log("[Autologin] Risposta completa ricevuta:", JSON.stringify(response));

      if (response && response.status === "success" && response.data && response.data.token) {
        const token = response.data.token;
        console.log("[Autologin] Token ricevuto:", token);

        localStorage.setItem("apiToken", token);
        const verifiedToken = localStorage.getItem("apiToken");

        if (verifiedToken === token) {
          console.log("[Autologin] Token salvato correttamente nel localStorage");
          setApiToken(token);
          setIsAuthenticated(true);

          // Carica le configurazioni aziendali usando configService
          try {
            console.log("[Autologin] Caricamento configurazioni aziendali...");
            const configurations = await configService.getConfigurations();
            
            // Combina i dati dell'azienda dalla risposta login con le configurazioni
            let companyData = {
              nome_azienda: "Azienda",
              piva: null,
              logo: null,
              orario: null,
              orarioBlocco: null,
              giornoChiusura: null,
              giornoApertura: null
            };

            // Se abbiamo dati dall'API di login, li usiamo
            if (response.data.aziende && response.data.aziende.length > 0) {
              const azienda = response.data.aziende[0].azienda;
              companyData = {
                id: azienda.id,
                nome_azienda: azienda.nome_azienda || configurations.nome || "Azienda",
                piva: azienda.piva || configurations.piva,
                host: azienda.host,
                dbname: azienda.dbname,
                logo: configurations.logo,
                orario: configurations.orario,
                orarioBlocco: configurations.orarioBlocco,
                giornoChiusura: configurations.giornoChiusura,
                giornoApertura: configurations.giornoApertura
              };
            } else {
              // Usa solo le configurazioni
              companyData = {
                nome_azienda: configurations.nome || "Azienda",
                piva: configurations.piva,
                logo: configurations.logo,
                orario: configurations.orario,
                orarioBlocco: configurations.orarioBlocco,
                giornoChiusura: configurations.giornoChiusura,
                giornoApertura: configurations.giornoApertura
              };
            }

            localStorage.setItem("companyInfo", JSON.stringify(companyData));
            setCompanyInfo(companyData);
            console.log("[Autologin] Info azienda salvate con configurazioni:", {
              nome: companyData.nome_azienda,
              orario: companyData.orario
            });
            
            // Emetti evento per aggiornare il sidebar
            window.dispatchEvent(new Event('companyInfoChanged'));
          } catch (configError) {
            console.error("[Autologin] Errore nel caricamento configurazioni:", configError);
            
            // Fallback: salva solo i dati base
            if (response.data.aziende && response.data.aziende.length > 0) {
              const azienda = response.data.aziende[0].azienda;
              const companyData = {
                id: azienda.id,
                nome_azienda: azienda.nome_azienda,
                piva: azienda.piva,
                host: azienda.host,
                dbname: azienda.dbname,
                orario: null,
                orarioBlocco: null,
                giornoChiusura: null,
                giornoApertura: null
              };
              localStorage.setItem("companyInfo", JSON.stringify(companyData));
              setCompanyInfo(companyData);
            }
          }

          const maskedToken = token.substring(0, 10) + "..." + token.substring(token.length - 5);
          console.log("[Autologin] Token generato e salvato:", maskedToken);
        } else {
          console.error("[Autologin] Errore nel salvataggio del token nel localStorage");
          setApiError("Errore nel salvataggio del token");
        }
      } else {
        console.error("[Autologin] Risposta non valida o token mancante:", response);
        if (!response) {
          setApiError("Risposta vuota dal server");
        } else if (response.status !== "success") {
          setApiError(`Errore di autenticazione: ${response.message || "motivo sconosciuto"}`);
        } else if (!response.data || !response.data.token) {
          setApiError("Token mancante nella risposta");
        }
      }
    } catch (error) {
      console.error("[Autologin] Errore durante l'autologin:", error);
      
      if (error.response) {
        console.error("[Autologin] Dettaglio risposta errore:", {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
      }
      
      if (error.response?.data?.message?.includes("CSRF")) {
        setApiError("Errore di sicurezza (CSRF). Controlla la configurazione del server.");
      } else {
        setApiError(`Errore di connessione: ${error.message}`);
      }
    } finally {
      setLoading(false);
      // Rilascia il lock SOLO dopo aver completato tutto
      isLoginInProgress.current = false;
    }
  };
  // Autologin per ottenere il token API
  useEffect(() => {
    // Carica companyInfo dal localStorage se presente
    const savedCompanyInfo = localStorage.getItem("companyInfo");
    if (savedCompanyInfo) {
      try {
        const companyData = JSON.parse(savedCompanyInfo);
        setCompanyInfo(companyData);
        console.log("[Init] CompanyInfo caricato dal localStorage:", companyData.nome_azienda);
      } catch (e) {
        console.error("[Init] Errore parsing companyInfo:", e);
      }
    }
    
    // Verifica se esiste già un token valido nel localStorage
    const existingToken = localStorage.getItem("apiToken");
    if (existingToken && !isLoginInProgress.current && !hasInitialized.current) {
      console.log("[Init] Token esistente trovato, verifico validità...");
      
      // Testa il token esistente con una chiamata API semplice
      configService.getDefaultClienteInterno()
        .then((response) => {
          if (response) {
            console.log("[Init] Token esistente è valido, riutilizzo...");
            setApiToken(existingToken);
            setIsAuthenticated(true);
            hasInitialized.current = true;
            return configService
              .getConfigurations()
              .then((cfg) => {
                if (!cfg) return;
                let parsed = {};
                try {
                  const raw = localStorage.getItem("companyInfo");
                  if (raw) parsed = JSON.parse(raw);
                } catch {
                  /* ignore */
                }
                const merged = {
                  ...parsed,
                  logo: cfg.logo ?? parsed.logo ?? null,
                  piva: cfg.piva ?? parsed.piva ?? null,
                  orario: cfg.orario ?? parsed.orario ?? null,
                  orarioBlocco: cfg.orarioBlocco ?? parsed.orarioBlocco ?? null,
                  giornoChiusura: cfg.giornoChiusura ?? parsed.giornoChiusura ?? null,
                  giornoApertura: cfg.giornoApertura ?? parsed.giornoApertura ?? null,
                };
                if (!merged.nome_azienda && cfg.nome) {
                  merged.nome_azienda = cfg.nome;
                }
                localStorage.setItem("companyInfo", JSON.stringify(merged));
                setCompanyInfo(merged);
                window.dispatchEvent(new Event("companyInfoChanged"));
              })
              .catch((e) =>
                console.error("[Init] Errore aggiornamento configurazioni (token esistente):", e)
              )
              .finally(() => setLoading(false));
          }
          throw new Error("Token non valido");
        })
        .catch(error => {
          console.log("[Init] Token esistente non valido o scaduto, eseguo nuovo autologin:", error.message);
          // Rimuovi il token non valido
          localStorage.removeItem("apiToken");
          
          // Esegui nuovo autologin solo se non è già in corso
          if (!isLoginInProgress.current && !hasInitialized.current) {
            autologin({
              setApiToken,
              setIsAuthenticated,
              setApiError,
              setLoading
            });
          }
        });
    } else if (!existingToken && !isLoginInProgress.current && !hasInitialized.current) {
      // Nessun token esistente, esegui autologin
      console.log("[Init] Nessun token esistente, eseguo autologin...");
      autologin({
        setApiToken,
        setIsAuthenticated,
        setApiError,
        setLoading
      });
    } else if (hasInitialized.current) {
      // Già inizializzato, imposta loading a false
      setLoading(false);
    }
    
    // Listener per gestire i token scaduti
    const handleTokenExpired = () => {
      console.log("[LoginContext] Token scaduto, eseguo logout...");
      logout();
    };
    
    window.addEventListener('tokenExpired', handleTokenExpired);
    
    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpired);
    };
    // eslint-disable-next-line
  }, []); // Array vuoto per eseguire solo al mount

  // Funzione per il login manuale
  const login = async (username, password, navigateCallback = null) => {
    try {
      console.log("[Login Manuale] Inizio login con username:", username);
      
      // Assicurati di avere un CSRF token fresco
      try {
        await apiService("/sanctum/csrf-cookie", "GET");
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (csrfError) {
        console.warn("[Login Manuale] Errore CSRF, proseguo:", csrfError);
      }

      const response = await apiService("/applicazioni/panificatori/login", "POST", {
        destlogin: username,
        destpsw: password,
      });

      console.log("[Login Manuale] Risposta ricevuta:", response);

      if (response && response.data) {
        console.log("[Login Manuale] Login riuscito per username:", username);
        
        // Controllo speciale: se destlogin e destpsw corrispondono a un indirizzo
        // e il risultato ha data.primario = true, chiama l'API def_clie_interno e apri pagina destinazioni
        if (response.data.primario === true) {
          console.log("[Login Manuale] Destinazione primaria rilevata, controllo cliente interno...");
          
          try {

            // Chiama l'API per ottenere il cliente interno di default
            const clienteInternoResponse = await configService.getDefaultClienteInterno();
            
            console.log("[Login Manuale] Cliente interno ricevuto:", clienteInternoResponse);
            
            // Se abbiamo una funzione di navigazione, apri la pagina delle destinazioni
            if (navigateCallback && typeof navigateCallback === 'function') {
              console.log("[Login Manuale] Apertura pagina destinazioni...");
              navigateCallback('/destinazioni');
              return true;
            }
          } catch (clienteInternoError) {
            console.error("[Login Manuale] Errore nel recupero cliente interno:", clienteInternoError);
            // Prosegui comunque con il login normale
          }
        }
        
        setIsAuthenticated(true);
        return true;
      } else {
        console.error("[Login Manuale] Errore: Credenziali non valide", response);
        throw new Error("Credenziali non valide");
      }
    } catch (error) {
      console.error("[Login Manuale] Errore durante il login manuale:", error);
      setApiError("Login fallito. Controlla le credenziali.");
      return false;
    }
  };

  // Funzione per ottenere un nuovo token (refresh)
  const refreshToken = async () => {
    console.log("[RefreshToken] Richiesta nuovo token...");
    
    // Reset dei flag per permettere un nuovo login
    hasInitialized.current = false;
    isLoginInProgress.current = false;
    setApiError(null);
    setLoading(true);
    
    // Richiama autologin
    await autologin({
      setApiToken,
      setIsAuthenticated,
      setApiError,
      setLoading
    });
  };

  const logout = () => {
    console.log("[Logout] Inizio procedura di logout...");
    
    // Pulisci tutto
    setIsAuthenticated(false);
    setApiToken(null);
    setApiError(null);
    setCompanyInfo(null);
    
    // Reset dei flag per permettere un nuovo autologin
    hasInitialized.current = false;
    isLoginInProgress.current = false;
    
    // Rimuovi i dati di sessione. Le credenziali «Ricordami» restano:
    // le toglie solo un nuovo accesso con il flag spento.
    localStorage.removeItem("apiToken");
    localStorage.removeItem("destinazione");
    localStorage.removeItem("codiceCliente");
    localStorage.removeItem("codiceDestinazione");
    localStorage.removeItem("companyInfo");
    
    console.log("[Logout] Logout completato");
  };

  return (
    <LoginContext.Provider
      value={{
        isAuthenticated,
        apiToken,
        loading,
        apiError,
        companyInfo,
        login,
        logout,
        refreshToken,  // Esporta la funzione refreshToken
      }}
    >
      {children}
    </LoginContext.Provider>
  );
};